import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import { Header } from './Dashboard';

const EMPTY = {
  key: '', tier: 'premium', name: '', durationDays: 30, priceInr: 299, perks: '',
  swipesPerDay: 100, likesPerDay: 1000, superLikesPerDay: 5, messagesPerDay: -1,
  seeWhoLikesYou: true, audioCall: true, videoCall: false, ageVisibility: true,
  gender: 'all', isSpecialOffer: false, offerUser: '', offerLabel: '',
  badge: '💎', isActive: true, order: 0,
};
const NUM_FIELDS = ['priceInr', 'durationDays', 'swipesPerDay', 'likesPerDay', 'superLikesPerDay', 'messagesPerDay', 'order'];

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null); // plan object or null

  const load = useCallback(async () => {
    const data = await api.get('/admin/plans');
    setPlans(data.plans);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    const payload = { ...form, perks: form.perks.split('\n').map((s) => s.trim()).filter(Boolean) };
    NUM_FIELDS.forEach((k) => { payload[k] = Number(form[k]); });
    if (!form.isSpecialOffer) payload.offerUser = null;
    if (!payload.offerUser) payload.offerUser = null;
    if (form._id) await api.patch(`/admin/plans/${form._id}`, payload);
    else await api.post('/admin/plans', payload);
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    await api.del(`/admin/plans/${id}`);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Header title="Membership Plans" sub="Create and manage subscription packages" />
        <button className="btn" onClick={() => setEditing({ ...EMPTY })}>+ New Plan</button>
      </div>

      <div style={styles.grid}>
        {plans.map((p) => (
          <div key={p._id} className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 30 }}>{p.badge}</div>
              <span className={`badge ${p.isActive ? 'active' : 'incomplete'}`}>{p.isActive ? 'Active' : 'Hidden'}</span>
            </div>
            <h3 style={{ margin: '12px 0 2px' }}>{p.name}</h3>
            <div style={{ color: 'var(--text-muted)', fontSize: 13, textTransform: 'capitalize' }}>{p.tier} • {p.durationDays} days</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--primary)', margin: '12px 0' }}>₹{p.priceInr}</div>
            <ul style={styles.perks}>
              {(p.perks || []).map((perk) => <li key={perk}>{perk}</li>)}
            </ul>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button className="btn sm muted" onClick={() => setEditing({ ...p, perks: (p.perks || []).join('\n') })}>Edit</button>
              <button className="btn sm danger" onClick={() => remove(p._id)}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing ? <PlanModal initial={editing} onSave={save} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

function PlanModal({ initial, onSave, onClose }) {
  const [form, setForm] = useState({ ...initial, offerUser: initial.offerUser?._id || initial.offerUser || '' });
  const [userQuery, setUserQuery] = useState(initial.offerUser?.name || '');
  const [userResults, setUserResults] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const field = (label, key, type = 'text') => (
    <div style={{ marginBottom: 12 }}>
      <label style={styles.label}>{label}</label>
      <input className="input" type={type} value={form[key]} onChange={(e) => set(key, e.target.value)} />
    </div>
  );
  const check = (label, key) => (
    <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <input type="checkbox" checked={!!form[key]} onChange={(e) => set(key, e.target.checked)} /> {label}
    </label>
  );

  const searchUsers = async (q) => {
    setUserQuery(q);
    if (q.length < 2) { setUserResults([]); return; }
    const d = await api.get(`/admin/users?search=${encodeURIComponent(q)}&limit=6`);
    setUserResults(d.users);
  };

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div className="card" style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>{form._id ? 'Edit Plan' : 'New Plan'}</h2>

        <div style={styles.formGrid}>
          {field('Key (unique)', 'key')}
          <div style={{ marginBottom: 12 }}>
            <label style={styles.label}>Tier</label>
            <select className="input" value={form.tier} onChange={(e) => set('tier', e.target.value)}>
              <option value="premium">Premium</option>
              <option value="gold">Gold</option>
              <option value="platinum">Platinum</option>
            </select>
          </div>
          {field('Name', 'name')}
          {field('Badge (emoji)', 'badge')}
          {field('Price (₹)', 'priceInr', 'number')}
          {field('Duration (days)', 'durationDays', 'number')}
        </div>

        <h4 style={styles.h4}>Limits (−1 = unlimited)</h4>
        <div style={styles.formGrid}>
          {field('Right swipes / day', 'swipesPerDay', 'number')}
          {field('Super likes / day', 'superLikesPerDay', 'number')}
          {field('Likes / day', 'likesPerDay', 'number')}
          {field('Messages / day', 'messagesPerDay', 'number')}
        </div>

        <h4 style={styles.h4}>Features</h4>
        <div style={styles.formGrid}>
          {check('See who likes you', 'seeWhoLikesYou')}
          {check('Audio calls', 'audioCall')}
          {check('Video calls', 'videoCall')}
          {check("See others' age", 'ageVisibility')}
        </div>

        <h4 style={styles.h4}>Targeting</h4>
        <div style={{ marginBottom: 12 }}>
          <label style={styles.label}>Gender package</label>
          <select className="input" value={form.gender} onChange={(e) => set('gender', e.target.value)}>
            <option value="all">Everyone</option>
            <option value="male">Men only</option>
            <option value="female">Women only</option>
            <option value="other">Other only</option>
          </select>
        </div>
        {check('Special offer (visible only to one user)', 'isSpecialOffer')}
        {form.isSpecialOffer ? (
          <div style={{ marginBottom: 12 }}>
            {field('Offer label', 'offerLabel')}
            <label style={styles.label}>Target user</label>
            <input className="input" placeholder="Search name or phone…" value={userQuery} onChange={(e) => searchUsers(e.target.value)} />
            {form.offerUser ? <div style={{ color: 'var(--like)', fontSize: 12, marginTop: 6 }}>✓ Selected: {userQuery}</div> : null}
            {userResults.length ? (
              <div style={styles.userResults}>
                {userResults.map((u) => (
                  <div key={u._id} style={styles.userResult} onClick={() => { set('offerUser', u._id); setUserQuery(`${u.name || ''} (+${u.phone})`); setUserResults([]); }}>
                    {u.name || '—'} · +{u.phone}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        <div style={{ marginBottom: 12 }}>
          <label style={styles.label}>Perks (one per line)</label>
          <textarea className="input" rows={3} value={form.perks} onChange={(e) => set('perks', e.target.value)} />
        </div>
        {check('Active (visible to users)', 'isActive')}

        <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn" onClick={() => onSave(form)}>Save Plan</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 },
  perks: { margin: 0, paddingLeft: 18, color: 'var(--text-dim)', fontSize: 13, lineHeight: 1.7 },
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, padding: 20 },
  modal: { width: 560, maxHeight: '90vh', overflowY: 'auto' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  label: { display: 'block', color: 'var(--text-dim)', fontSize: 13, fontWeight: 600, marginBottom: 6 },
  h4: { margin: '16px 0 8px', color: '#fff', fontSize: 14 },
  userResults: { background: 'var(--card-alt)', borderRadius: 8, marginTop: 6, overflow: 'hidden' },
  userResult: { padding: '10px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)', color: 'var(--text-dim)' },
};
